/**
 * ChurnRetentionMetrics
 *
 * Churn & Retention Analysis section of the analytics dashboard.
 * Displays non-renewing users, cancellation status, and historical metrics.
 */
import React from 'react';
import { 
  XCircle, 
  UserX, 
  Activity,
  DollarSign
} from 'lucide-react';
import AnalyticsTooltip from './AnalyticsTooltip';

const ChurnRetentionMetrics = ({ analyticsData, formatCurrency }) => {
  return (
    <>
      {/* Churn & Retention Analysis */}
      <div className="mb-6">
        <h3 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Churn & Retention Analysis</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {/* Non-Renewing Paid Users */}
          <AnalyticsTooltip content="Non-Renewing Paid Users - Currently paying customers who have cancelled but are still active until their billing period ends." id="non-renewing-paid-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-lg mr-3">
                  <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Non-Renewing Paid Users</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {(analyticsData.summary?.totalNonRenewingPaidUsers || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-red-600 dark:text-red-400">will churn soon</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>

          {/* Cancelling Soon */}
          <AnalyticsTooltip content="Cancelling Soon - Number of paying customers who have cancelled their subscription but are still active until their billing period ends. These customers will churn unless they reactivate their subscription." id="cancelling-soon-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg mr-3">
                  <UserX className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Cancelling Soon</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {(analyticsData.summary?.payingCustomersScheduledToCancel || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-yellow-600 dark:text-yellow-400">will cancel at period end</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>

          {/* Deleted Paying Customers */}
          <AnalyticsTooltip content="Deleted Paying Customers - Number of customers who had active paid subscriptions and later deleted their accounts. These were actual paying customers (not just trial users) who contributed revenue but have since churned by deleting their accounts." id="deleted-paying-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-lg mr-3">
                  <UserX className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Deleted Paying</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {(analyticsData.summary?.deletedPayingCustomers || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-red-600 dark:text-red-400">customers deleted</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>

          {/* Deleted Trial Users */}
          <AnalyticsTooltip content="Deleted Trial Users - Number of users who had premium trials but never converted to paying customers, and later deleted their accounts. These users tried the premium features but didn't convert and then churned." id="deleted-trial-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 dark:bg-orange-900/20 rounded-lg mr-3">
                  <UserX className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Deleted Trial</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {(analyticsData.summary?.deletedTrialUsers || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-orange-600 dark:text-orange-400">trial users deleted</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>
        </div>
      </div>

      {/* Historical Metrics */}
      <div className="mb-4">
        <h3 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Historical Metrics</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {/* Total Customers Ever */}
          <AnalyticsTooltip content="Total Customers Ever Acquired - All customers who have ever subscribed to your service, including both active customers and those who have since cancelled or deleted their accounts." id="total-ever-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-indigo-100 dark:bg-indigo-900/20 rounded-lg mr-3">
                  <Activity className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Ever Acquired</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {(analyticsData.summary?.totalCustomersEverAcquired || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-indigo-600 dark:text-indigo-400">lifetime customers</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>

          {/* Revenue from Deleted Users */}
          <AnalyticsTooltip content="Revenue from Deleted Users - Total revenue generated by users who later deleted their accounts. This represents lost customer lifetime value." id="deleted-revenue-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 dark:bg-orange-900/20 rounded-lg mr-3">
                  <DollarSign className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Lost Revenue</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(analyticsData.summary?.deletedUsersRevenue || 0)}
                  </p>
                  <p className="text-xs text-orange-600 dark:text-orange-400">from deleted accounts</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>
        </div>
      </div>
    </>
  );
};

export default ChurnRetentionMetrics;


